import { useEffect, useState } from "react";

import {
  Button,
  getUserIdFromRefreshToken,
  InfoPage,
  isLoggedIn,
  Link,
  LogSentryException,
  PageContainer,
  Spinner,
} from "@shapr3d/frontend-lib";
import { useMutation } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { TOKEN_AUDIENCE, UMSrequestClient } from "../../stores/requestClient";
import { SearchParam } from "../../types";
import { saveLoggedInUser } from "../../utils/authUtils";
import { sanitizeRedirectUrl } from "../../utils/httpUtils";
import useTracker from "../../utils/useTracker";

import style from "./AuthPage.module.css";

export type AuthResponseData = {
  id: string;
  email: string;
  refreshToken: string;
  accessToken: string;
  emailToken?: string;
};
export const useVerifyAuthHandoff = () => {
  return useMutation({
    mutationKey: ["verify-auth-handoff"],
    mutationFn: async (authHandoffToken: string) => {
      const extraHeaders: { [key: string]: string } = {};

      const response = await UMSrequestClient.publicRequest({
        method: "POST",
        url: "/auth/handoff/verify",
        extraHeaders,
        body: {
          token: authHandoffToken,
          audience: TOKEN_AUDIENCE,
          issueEmailToken: true,
        },
      });

      return response.json<AuthResponseData>();
    },
  });
};

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const tracker = useTracker();

  const [token, setToken] = useState("");
  useEffect(() => {
    if (location.hash) {
      const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
      const newToken = hashParams.get("token") ?? "error"; // Empty and special 'error' token both means error
      setToken(newToken);
      navigate(location.pathname + location.search, { replace: true });
    } else if (token === "") {
      setToken((token) => {
        if (token !== "") return token;
        return "error";
      });
    }
  }, [location, token, navigate]);

  const { mutateAsync: mutateVerifyAuthHandoff, isError } = useVerifyAuthHandoff();

  const [searchParams] = useSearchParams();
  const redirectURL = decodeURIComponent(searchParams.get("redirect_url") ?? "");
  const sanitizedRedirectUrl = sanitizeRedirectUrl(redirectURL).toString();
  const isRedirectURLError = redirectURL != sanitizedRedirectUrl;

  useEffect(() => {
    if (token !== "" && token !== "error" && redirectURL && !isRedirectURLError) {
      const verifyToken = async () => {
        try {
          await mutateVerifyAuthHandoff(token, {
            onSuccess: (data) => {
              const previouslyLoggedinUserId = getUserIdFromRefreshToken();

              if (
                isLoggedIn() &&
                previouslyLoggedinUserId &&
                previouslyLoggedinUserId !== data.id
              ) {
                tracker.reset();
              }
              saveLoggedInUser(data);
              const newRedirectURL = new URL(redirectURL);
              if (data.emailToken && newRedirectURL.searchParams.has(SearchParam.InvitationHash)) {
                newRedirectURL.searchParams.append(SearchParam.EmailToken, data.emailToken);
              }
              window.location.replace(newRedirectURL.toString());
            },
          });
        } catch (err) {
          LogSentryException({ messagePrefix: "Auth handoff verify error", error: err });
        }
      };
      verifyToken();
    }
  }, [navigate, token, redirectURL, mutateVerifyAuthHandoff, isRedirectURLError, tracker]);

  if (isRedirectURLError) {
    return (
      <InfoPage
        variant="warning"
        title="Something went wrong."
        description={
          <>
            Please try again or follow{" "}
            <Link href={"https://support.shapr3d.com/hc/en-us"}>this link</Link> to submit a support
            ticket.
          </>
        }
      />
    );
  }

  if (isError || token === "error") {
    return (
      <InfoPage
        variant="warning"
        title={
          <>
            Something went wrong,
            <br />
            please log in again.
          </>
        }
        primaryCTA={
          <Button
            onClick={() => {
              window.location.replace(redirectURL);
            }}
          >
            Back to log in
          </Button>
        }
      />
    );
  }

  return (
    <PageContainer>
      <div className={style.container}>
        <Spinner />
      </div>
    </PageContainer>
  );
}
